/** Turning a failed request into a sentence an operator can act on.
 *
 * Some refusals from this API are WRITTEN for the person reading them. The clearest example is
 * `OVERRIDE_ACTIVE` on a door delete: the server explains that the door is standing in a
 * persistent override and that the override has to be released first. Replacing that with
 * "please try again" would turn an explained refusal into a mystery, and the operator would
 * retry forever against a rule that will never let them through.
 *
 * Everything else - a 500, a proxy's HTML error page, a dropped connection - is written for a
 * log rather than for a person, and can carry internals that have no business on screen (the
 * server deliberately answers `INTERNAL` / "an internal error occurred" for exactly that
 * reason: a driver exception leaks schema, host names and sometimes credentials). Those become
 * one plain sentence supplied by the caller.
 *
 * But an opaque failure with NOTHING to quote is a dead end for the person in front of it. The
 * server logged that failure against a correlation id, so when there is one it is appended to
 * the sentence. That single token is the difference between "it is broken" and an engineer
 * finding the actual stack trace in the log in one search.
 */
import { ApiError } from "../api/client.js";

/** Codes whose server-side message is intended for an operator and is repeated verbatim. */
const EXPLAINED_CODES: ReadonlySet<string> = new Set([
  "OVERRIDE_ACTIVE",
  "NOT_FOUND",
  "VALIDATION",
  "CONFLICT",
  "FORBIDDEN",
  "DOOR_DISABLED",
]);

export function operatorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && EXPLAINED_CODES.has(error.code)) return error.message;
  if (error instanceof ApiError && error.correlationId) {
    return `${fallback} Reference ${error.correlationId}.`;
  }
  return fallback;
}
